/**
 * localePreference
 *
 * Persists the user's explicit language choice and resolves the language the
 * TranslationBanner may suggest on a french (unprefixed) URL.
 *
 * The served language only ever follows the URL (see LanguageProvider): a
 * stored preference or the browser language never redirect on their own, they
 * only drive the suggestion. A stored 'fr' is the opt-out that silences the
 * banner for good. Storage failures (e.g. Safari private mode) degrade to
 * per-visit browser-language detection.
 */
import { NAVIGATION } from '../containers/App/constants';
import { getCurrentRootPage, isCrawler } from '../helpers';
import { isMobileApp } from '../helpers/app';
import {
  getLocaleFromPathname,
  stripLocale,
  URL_DEFAULT_LOCALE,
  URL_LOCALES,
} from './i18nUrls';

const PREFERRED_LOCALE_KEY = 'ttt:preferredLocale';

/**
 * Pages the suggestion is worth making on: the landing pages, the ones a
 * visitor arrives on from a search engine and that exist translated. The
 * funnel, the account and the admin are left out — mid-booking is no time to
 * be offered another URL.
 */
const LANDING_ROOT_PAGES = [
  NAVIGATION.HOME,
  NAVIGATION.SEARCH,
  NAVIGATION.CITY,
  NAVIGATION.COMPANY,
];

export const getPreferredLocale = () => {
  try {
    return localStorage.getItem(PREFERRED_LOCALE_KEY);
  } catch {
    return null;
  }
};

export const setPreferredLocale = (locale) => {
  try {
    localStorage.setItem(PREFERRED_LOCALE_KEY, locale);
  } catch {
    // noop
  }
};

/**
 * @description the language worth suggesting for a pathname, or null.
 * Only on unprefixed (french) URLs and only on the landing pages
 * (LANDING_ROOT_PAGES), never for crawlers (the suggestion must not end up in
 * prerendered snapshots), never inside the mobile app's WebView (the language
 * is the phone's there, and there is no URL to send anyone to), and never once
 * the user opted out. An earlier explicit choice wins over the browser
 * language.
 * @param {string} pathname
 * @returns {string | null} one of URL_LOCALES
 */
export const getSuggestedLocale = (pathname) => {
  if (getLocaleFromPathname(pathname)) return null;

  const rootPage = getCurrentRootPage(stripLocale(pathname));

  if (!LANDING_ROOT_PAGES.includes(rootPage)) return null;

  // isMobileApp reads the string straight, so it must never be undefined.
  const userAgent = window.navigator?.userAgent ?? '';

  if (isCrawler(userAgent)) return null;
  if (isMobileApp(userAgent)) return null;

  const storedLocale = getPreferredLocale();

  if (storedLocale === URL_DEFAULT_LOCALE) return null;
  if (URL_LOCALES.includes(storedLocale)) return storedLocale;

  const browserLocale = window.navigator?.language?.slice(0, 2);

  return URL_LOCALES.includes(browserLocale) ? browserLocale : null;
};
